import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import ServiceHeader from "@modules/Invoice/AddEditInvoice/components/InvoiceServices/ServiceHeader";
import InvoiceTotal from "@modules/Invoice/AddEditInvoice/components/InvoiceServices/InvoiceTotal";
import PulseLoader from "react-spinners/PulseLoader";
import { WarningPopUp, Image, Button } from "hesus-ui-kit";
import {
  useGetMultiUserCurrenciesByCountryId,
  useGetGeneralParameters,
} from "@hooks/useGeneralParams";
import {
  renderPriceWithCurrency,
  renderPriceWithCurrencyWithoutUnit,
  addSpace,
} from "@utils/utils";
import { GridRow, GridCellValue, GridCell } from "../../InvoiceView.styled";
import "../../../AddEditInvoice/AddEditInvoice.scss";
import infoIcon from "@icons/ic-info.svg";

const InvoiceServices = ({
  t,
  serviceItems,
  extraItems,
  serviceLoading,
  invoiceData,
}) => {
  const generalParams = useGetGeneralParameters();
  const exchangeRates = invoiceData?.exchangeRates;
  const countryId = invoiceData?.country?.id;
  const userCurrencyObject = useGetMultiUserCurrenciesByCountryId(countryId);
  const currencyList = generalParams.countryCurrency;

  const parseDecimal = (value) => {
    if (value === null || value === undefined || value === "") {
      return NaN;
    }
    return Number(String(value).replace(",", "."));
  };
  const getItemTotal = (item, service) => {
    if (item.invoicedUnit === null || item.invoicedUnit === "") {
      return "-";
    }
    const quantity = parseDecimal(item.invoicedQuantity);
    const sellingPrice = parseDecimal(item.sellingPrice);
    if (Number.isNaN(quantity)) {
      return "-";
    }
    if (item.sellingPrice === null || item.sellingPrice === "") {
      return "-";
    }
    if (Number.isNaN(sellingPrice)) {
      return "-";
    }
    let total = sellingPrice * quantity;

    const quoteCurrencyId = Number(service.quoteService?.currency?.id);
    const invoiceCurrencyId = Number(invoiceData.currency.id);

    if (quoteCurrencyId !== invoiceCurrencyId) {
      const exchangeRate = exchangeRates.find(
        (rate) =>
          Number(rate.currencyFrom) === quoteCurrencyId &&
          Number(rate.currencyTo) === invoiceCurrencyId
      );

      const rate = Number(exchangeRate?.exchangeRate);

      if (!rate || Number.isNaN(rate)) {
        return "-";
      }

      total *= rate;
    }

    return total.toFixed(2);
  };

  const getExtraItemTotal = (item) => {
    const quantity = parseDecimal(item.invoicedQuantity);
    const sellingPrice = parseDecimal(item.sellingPrice);

    if (
      item.invoicedQuantity === "" ||
      item.invoicedQuantity === null ||
      item.sellingPrice === "" ||
      item.sellingPrice === null ||
      Number.isNaN(quantity) ||
      Number.isNaN(sellingPrice)
    ) {
      return "-";
    }

    return (quantity * sellingPrice).toFixed(2);
  };

  const getGrandTotalWithoutTaxes = () => {
    let grandTotal = 0;
    let hasValidTotal = false;

    // Existing quote items
    (serviceItems ?? []).forEach((service) => {
      (service.quoteItems ?? []).forEach((item) => {
        const total = getItemTotal(item, service);

        if (total !== "-") {
          grandTotal += Number(total);
          hasValidTotal = true;
        }
      });
    });

    // Optional additional items
    (extraItems ?? []).forEach((item) => {
      const total = getExtraItemTotal(item);

      if (total !== "-") {
        grandTotal += Number(total);
        hasValidTotal = true;
      }
    });

    return hasValidTotal ? grandTotal.toFixed(2) : "-";
  };

  const getTaxesAmount = () => {
    const grandTotal = getGrandTotalWithoutTaxes();

    if (grandTotal === "-") {
      return "-";
    }
    const vatRate = invoiceData?.vatRate;
    const rate = Number(vatRate?.calculationRate) || 0;
    return Number(grandTotal * rate).toFixed(2);
  };

  const getTotalIncludingTaxes = () => {
    const grandTotal = getGrandTotalWithoutTaxes();
    if (grandTotal === "-") {
      return "-";
    }
    const taxes = getTaxesAmount();

    return (Number(grandTotal) + Number(taxes)).toFixed(2);
  };

  const serviceItemColumns =
    "minmax(0, 2fr) minmax(180px, 0.8fr) minmax(150px, 0.7fr)";

  return (
    <>
      <GridRow
        background="#008395"
        borderRadius="5px 5px 0 0"
        column="1fr auto"
      >
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_SERVICES")}
        </GridCell>
      </GridRow>
      {invoiceData.clientCommentBefore && (
        <GridRow column="1fr" padding="10px" gap="1rem">
          <GridCellValue fontSize="14px" color="#474747">
            <div
              dangerouslySetInnerHTML={{
                __html: invoiceData.clientCommentBefore,
              }}
            ></div>
          </GridCellValue>
        </GridRow>
      )}
      <GridRow
        padding="0"
        column="1fr"
        boxShadow="0px 1px 1px 1px #e5e5e5"
        borderRadius="0 0 5px 5px"
        gap="0"
      >
        {serviceLoading && (
          <GridRow padding="10px 12px">
            <GridCell
              color="#777"
              fontSize="16px"
              fontWeight="400"
              fontStyle="italic"
            >
              <div className="suggestions-list-loading">
                <PulseLoader color="#1bace4" size={15} />
              </div>
            </GridCell>
          </GridRow>
        )}
        {!serviceLoading && (
          <ServiceHeader column={serviceItemColumns} page="view" />
        )}

        <GridRow
          column="1fr"
          padding={
            extraItems.length > 0 ? "0 10px 0px 10px" : "0 10px 10px 10px"
          }
          gap="0px"
        >
          {serviceItems.length === 0 &&
            extraItems.length === 0 &&
            !serviceLoading && (
              <GridRow
                padding="10px 12px"
                column="1fr"
                gap="0px"
                border="1px solid #b7c9df"
                borderTop="1px solid #b7c9df"
                borderBottom="1px solid #b7c9df"
                borderRadius="4px 4px 0 0"
              >
                <GridCell
                  color="#777"
                  fontSize="16px"
                  fontWeight="400"
                  fontStyle="italic"
                  textAlign="center"
                >
                  {t("CLIENT_INVOICING_NO_SERVICES")}
                </GridCell>
              </GridRow>
            )}
          {serviceItems.map((service, serviceIndex) => (
            <GridRow
              key={
                "invoice-service-" +
                serviceIndex +
                "-" +
                service.quoteService.id
              }
              column="1fr"
              padding="0"
              gap="0px"
              border="1px solid #b7c9df"
              borderTop={serviceIndex === 0 ? "1px solid #b7c9df" : "none"}
              borderRadius={serviceIndex === 0 ? "4px 4px 0 0" : "0"}
              overflow="hidden"
            >
              {/* Quote Service Header */}
              <GridRow
                column="1fr"
                padding="7px 10px"
                background="#D1E6F7"
                borderBottom="1px solid #D1E6F7"
                gap="0"
              >
                <GridCell color="#0A4986" fontSize="16px" fontWeight="700">
                  {service.quoteService.label}
                </GridCell>
              </GridRow>

              {/* Quote Items */}
              {service.quoteItems.map((item, index) => {
                return (
                  <GridRow
                    key={"quoteItems" + index + "-" + item.quoteItem}
                    column={serviceItemColumns}
                    padding="0"
                    background="#fff"
                    gap="0"
                    borderBottom={
                      index === service.quoteItems.length - 1
                        ? "1px solid #b7c9df"
                        : "1px solid #d5dce5"
                    }
                  >
                    {/* Item + Price */}
                    <GridRow column="1fr auto" padding="7px 12px" gap="15px">
                      <GridCell
                        color="#474747"
                        fontSize="15px"
                        fontWeight="700"
                      >
                        <div>{item.serviceLabel}</div>

                        {item.clientComment && (
                          <div
                            style={{
                              color: "#767676",
                              fontSize: "14px",
                              fontStyle: "italic",
                              fontWeight: "400",
                              marginTop: "3px",
                            }}
                          >
                            {item.clientComment}
                          </div>
                        )}
                      </GridCell>

                      <GridCell
                        color="#474747"
                        fontSize="15px"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <div style={{ textAlign: "right" }}>
                          <div>
                            {renderPriceWithCurrency(
                              item.sellingPrice.toFixed(2),
                              t(item.sellingUnit),
                              invoiceData.currency.symbol,
                              invoiceData.currency.position
                            )}
                          </div>
                          {service.quoteService.currency.id !==
                            invoiceData.currency.id && (
                            <div
                              style={{
                                color: "#FF981E",
                                marginLeft: "8px",
                                fontStyle: "italic",
                                fontSize: "14px",
                              }}
                            >
                              {renderPriceWithCurrency(
                                item.quoteItem.sellingPrice.toFixed(2),
                                t(item.quoteItem.unit),
                                service.quoteService.currency.symbol,
                                service.quoteService.currency.position
                              )}
                            </div>
                          )}
                        </div>
                      </GridCell>
                    </GridRow>

                    {/* Quantity */}

                    <GridCell
                      color="#474747"
                      fontSize="15px"
                      fontWeight="400"
                      display="grid"
                      alignItems="center"
                      style={{
                        borderLeft: "1px solid #b7c9df",
                        gridTemplateColumns: "1fr auto 1fr",
                      }}
                    >
                      <div />
                      <div>
                        {item.invoicedQuantity}
                        {" " + t(item.invoicedUnit)}
                      </div>
                      <div style={{ justifySelf: "end", paddingRight: "10px" }}>
                        <Image
                          src={infoIcon}
                          alt={t("CLIENT_INVOICE_INFO")}
                          title={t("CLIENT_INVOICE_INFO")}
                          className="view-invoice__image-view-icon"
                        />
                      </div>
                    </GridCell>           

                    {/* Total */}
                    <GridCell
                      color="#474747"
                      fontSize="15px"
                      fontWeight="700"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      style={{
                        borderLeft: "1px solid #b7c9df",
                        paddingRight: "14px",
                        background: "#EDEDED",
                      }}
                    >
                      {renderPriceWithCurrencyWithoutUnit(
                        addSpace(getItemTotal(item, service)),
                        invoiceData.currency.symbol,
                        invoiceData.currency.position
                      )}
                    </GridCell>
                  </GridRow>
                );
              })}
            </GridRow>
          ))}
        </GridRow>
      </GridRow>
      {extraItems.length > 0 && (
        <GridRow column="1fr" padding="0 10px 10px 10px" gap="0">
          {/* Additional services header */}
          <GridRow
            column="1fr"
            padding="7px 10px"
            background="#D1E6F7"
            border="1px solid #D1E6F7"
            borderBottom="1px solid #b7c9df"
            gap="0"
          >
            <GridCell color="#0A4986" fontSize="16px" fontWeight="700">
              {t("CLIENT_INVOICING_ADDITIONAL_SERVICES")}
            </GridCell>
          </GridRow>

          {extraItems.map((item, index) => {
            return (
              <GridRow
                key={`extra-item-${index}`}
                column={serviceItemColumns}
                padding="0"
                background="#fff"
                gap="0"
                border="1px solid #b7c9df"
                borderBottom={
                  index === extraItems.length - 1
                    ? "1px solid #b7c9df"
                    : "1px solid #d5dce5"
                }
              >
                {/* Service + Price + Unit + comment */}
                <div>
                  <GridRow column="1fr auto" padding="10px 12px" gap="15px">
                    <GridCell color="#333" fontSize="16px" fontWeight="700">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <GridCell
                          color="#474747"
                          fontSize="15px"
                          fontWeight="700"
                        >
                          {/* Service label */}
                          <div>
                            {item.hesusCatalog?.id
                              ? item?.hesusCatalog?.label
                              : item.serviceLabel}
                          </div>
                          {item.clientComment && (
                            <div
                              style={{
                                color: "#767676",
                                fontSize: "14px",
                                fontStyle: "italic",
                                fontWeight: "400",
                                marginTop: "3px",
                              }}
                            >
                              {item.clientComment}
                            </div>
                          )}
                        </GridCell>
                      </div>
                    </GridCell>

                    {/* Price + Unit */}
                    <GridCell
                      color="#474747"
                      fontSize="15px"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <div style={{ textAlign: "right" }}>
                        <div>
                          {renderPriceWithCurrency(
                            item.sellingPrice.toFixed(2),
                            t(item.sellingUnit),
                            invoiceData.currency.symbol,
                            invoiceData.currency.position
                          )}
                        </div>
                      </div>
                    </GridCell>
                  </GridRow>
                </div>

                {/* Quantity */}
                <GridCell
                  color="#474747"
                  fontSize="15px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    borderLeft: "1px solid #b7c9df",
                    gap: "8px",
                  }}
                >
                  {item.invoicedQuantity}
                  {" " + t(item.invoicedUnit)}
                </GridCell>

                {/* Total */}
                <GridCell
                  color="#474747"
                  fontSize="15px"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  style={{
                    borderLeft: "1px solid #b7c9df",
                    paddingRight: "14px",
                    background: "#D1E6F780",
                  }}
                >
                  {renderPriceWithCurrencyWithoutUnit(
                    addSpace(getExtraItemTotal(item)),
                    invoiceData.currency.symbol,
                    invoiceData.currency.position
                  )}
                </GridCell>
              </GridRow>
            );
          })}
        </GridRow>
      )}
      {(serviceItems.length > 0 || extraItems.length > 0) &&
        !serviceLoading && (
          <InvoiceTotal
            page="view"
            currencyList={currencyList}
            invoiceCurrency={userCurrencyObject[invoiceData.currency.id]}
            exchangeRates={exchangeRates}
            totalExculdingTxes={renderPriceWithCurrencyWithoutUnit(
              addSpace(getGrandTotalWithoutTaxes()),
              invoiceData.currency.symbol,
              invoiceData.currency.position
            )}
            taxesAmount={renderPriceWithCurrencyWithoutUnit(
              addSpace(getTaxesAmount()),
              invoiceData.currency.symbol,
              invoiceData.currency.position
            )}
            totalInculdingTxes={renderPriceWithCurrencyWithoutUnit(
              addSpace(getTotalIncludingTaxes()),
              invoiceData.currency.symbol,
              invoiceData.currency.position
            )}
            selectedVatDetail={invoiceData?.vatRate?.label}
            currencyId={invoiceData.currency.id}
          />
        )}

      {invoiceData.clientCommentAfter && (
        <GridRow column="1fr" padding="10px" gap="1rem">
          <GridCellValue fontSize="14px" color="#474747">
            <div
              dangerouslySetInnerHTML={{
                __html: invoiceData.clientCommentAfter,
              }}
            ></div>
          </GridCellValue>
        </GridRow>
      )}
    </>
  );
};

InvoiceServices.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  serviceLoading: PropTypes.bool,
  updateRefreshStatus: PropTypes.func,
};

InvoiceServices.defaultProps = {
  isEditMode: false,
  id: null,
  updateRefreshStatus: null,
  serviceLoading: false,
};

export default withTranslation()(InvoiceServices);
